// Perspective Context - Allows Super Admins to view the platform as different internal roles
// NOTE: This is for UI mirroring only - does NOT change actual permissions or data access
// Client perspective is NOT available for privacy/data integrity reasons

import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuth, ROLES } from './AuthContext';

const PerspectiveContext = createContext(null);

// Only internal roles can be simulated - NO CLIENT ACCESS
export const AVAILABLE_PERSPECTIVES = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  CONSULTANT: 'CONSULTANT'
};

const PERSPECTIVE_STORAGE_KEY = 'fedbd_perspective';

const PERSPECTIVE_USER_KEY = 'fedbd_perspective_user';

export function PerspectiveProvider({ children }) {
  const { user } = useAuth();
  
  // Start with default perspective - we'll restore from localStorage in useEffect once user is known
  const [activePerspective, setActivePerspective] = useState(AVAILABLE_PERSPECTIVES.SUPER_ADMIN);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Only Super Admins can use perspective switching
  const canSwitchPerspective = user?.role === ROLES.SUPER_ADMIN;
  
  // Restore perspective from localStorage once user is loaded
  useEffect(() => {
    if (!user?.id) return;
    
    const stored = localStorage.getItem(PERSPECTIVE_STORAGE_KEY);
    const storedUserId = localStorage.getItem(PERSPECTIVE_USER_KEY);
    
    // If the stored perspective is for this same user, restore it
    if (stored && storedUserId === user.id && Object.values(AVAILABLE_PERSPECTIVES).includes(stored)) {
      setActivePerspective(stored);
    } else if (storedUserId && storedUserId !== user.id) {
      // Different user - reset perspective
      setActivePerspective(AVAILABLE_PERSPECTIVES.SUPER_ADMIN);
      localStorage.removeItem(PERSPECTIVE_STORAGE_KEY);
    }
    
    // Store current user ID for next check
    localStorage.setItem(PERSPECTIVE_USER_KEY, user.id);
    
    // If not a Super Admin, force reset to default
    if (user.role !== ROLES.SUPER_ADMIN) {
      setActivePerspective(AVAILABLE_PERSPECTIVES.SUPER_ADMIN);
      localStorage.removeItem(PERSPECTIVE_STORAGE_KEY);
    }
    
    setIsInitialized(true);
  }, [user?.id, user?.role]);
  
  const switchPerspective = useCallback((perspective) => {
    if (!canSwitchPerspective) {
      console.warn('Perspective switching is only available for Super Admins');
      return;
    }
    
    if (!Object.values(AVAILABLE_PERSPECTIVES).includes(perspective)) {
      console.warn('Invalid perspective:', perspective);
      return;
    }
    
    setActivePerspective(perspective);
    // Persist to localStorage
    localStorage.setItem(PERSPECTIVE_STORAGE_KEY, perspective);
  }, [canSwitchPerspective]);
  
  const resetPerspective = useCallback(() => {
    setActivePerspective(AVAILABLE_PERSPECTIVES.SUPER_ADMIN);
    localStorage.removeItem(PERSPECTIVE_STORAGE_KEY);
  }, []);
  
  // Determine the effective role for UI rendering
  const getEffectiveRole = useCallback(() => {
    if (!canSwitchPerspective) {
      return user?.role;
    }
    
    switch (activePerspective) {
      case AVAILABLE_PERSPECTIVES.CONSULTANT:
        return ROLES.CONSULTANT;
      case AVAILABLE_PERSPECTIVES.SUPER_ADMIN:
      default:
        return ROLES.SUPER_ADMIN;
    }
  }, [canSwitchPerspective, activePerspective, user?.role]);
  
  // Check if currently in mock view (not the default Super Admin view)
  const isInMockView = canSwitchPerspective && activePerspective !== AVAILABLE_PERSPECTIVES.SUPER_ADMIN;
  
  const value = {
    activePerspective,
    switchPerspective,
    resetPerspective,
    canSwitchPerspective,
    isInMockView,
    getEffectiveRole,
    AVAILABLE_PERSPECTIVES
  };
  
  return (
    <PerspectiveContext.Provider value={value}>
      {children}
    </PerspectiveContext.Provider>
  );
}

export function usePerspective() {
  const context = useContext(PerspectiveContext);
  if (!context) {
    throw new Error('usePerspective must be used within a PerspectiveProvider');
  }
  return context;
}

export default PerspectiveContext;
